/**
 * Instance-level customer type, stored as a plain string at `global/customerType`.
 *
 * Set by a superadmin under Instansinställningar. Clients use it to hide
 * fields that do not apply to the customer. A missing or unknown value means
 * "show everything", so an older client is never affected by a newer type.
 * The database rule only requires a non-empty string; the allowed values
 * live here.
 */
export const CUSTOMER_TYPES = ['stiftelse', 'bostadsrattsforening', 'fastighetsagare'] as const;

export type CustomerType = typeof CUSTOMER_TYPES[number];

export const CUSTOMER_TYPE_LABELS: Record<CustomerType, string> = {
    stiftelse: 'Stiftelse',
    bostadsrattsforening: 'Bostadsrättsförening',
    fastighetsagare: 'Fastighetsägare',
};

export const CUSTOMER_TYPE_PATH = 'global/customerType';

/** Narrow an arbitrary database value to a known customer type, or null. */
export function parseCustomerType(value: unknown): CustomerType | null {
    return typeof value === 'string' && (CUSTOMER_TYPES as readonly string[]).includes(value)
        ? (value as CustomerType)
        : null;
}

/** The instance's word for the people in the tenant register, as labels. */
export interface TenantTerm {
    /** "Hyresgäst", or "Medlem / hyresgäst" in a BRF. */
    singular: string;
    /** "Hyresgäster", or "Medlemmar / hyresgäster" in a BRF. */
    plural: string;
    /** The plural inside running text: "Hyresgäster", or "Medlemmar och hyresgäster" in a BRF. */
    pluralInText: string;
}

const HYRESGAST_TERM: TenantTerm = { singular: 'Hyresgäst', plural: 'Hyresgäster', pluralInText: 'Hyresgäster' };
const BRF_TENANT_TERM: TenantTerm = { singular: 'Medlem / hyresgäst', plural: 'Medlemmar / hyresgäster', pluralInText: 'Medlemmar och hyresgäster' };

/**
 * A bostadsrättsförening's register is mostly members, so it says "Medlem /
 * hyresgäst"; every other customer type, and an unset one, says "Hyresgäst".
 * Labels only: sentences avoid the noun so they read for every customer type.
 */
export function tenantTerm(customerType: CustomerType | null): TenantTerm {
    return customerType === 'bostadsrattsforening' ? BRF_TENANT_TERM : HYRESGAST_TERM;
}
